import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiArrowRight, FiMapPin } from 'react-icons/fi';
import './FeaturedDestinations.css';
import Api from "../../../Api.js";

export default function FeaturedDestinations() {
  const [destinationsData, setDestinationsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchDestinations = async () => {
      try {
        setLoading(true);
        const response = await Api.get('/wedding/destinations', {
          signal: abortController.signal,
        });

        const payload = response.data || response;

        if (payload.status && Array.isArray(payload.data) && payload.data.length > 0) {
          const mappedDestinations = payload.data.map((item) => ({
            id: item.id,
            name: item.name,
            image: item.featured_photo_url?.startsWith('http')
              ? item.featured_photo_url
              : `https://admin.jayrajgroup.com${item.featured_photo_url || ''}`,
            link: `/destination-wedding-packages/${item.id}`,
            state: item.state?.name,
            city: item.city?.name,
            shortDescription: item.short_description,
            packagesCount: item.packages_count,
          }));
          setDestinationsData(mappedDestinations);
        } else {
          console.warn('API returned no destinations.');
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
          console.error('Failed to fetch destinations:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDestinations();
    return () => abortController.abort();
  }, []);

  if (loading) {
    return (
      <section className="featured-destinations-section">
        <div className="featured-container">
          <div className="featured-header">
            <h2 className="heading-playfair-italic main-title">Best Wedding Places in India</h2>
          </div>
          <div className="loading-state">Loading luxury destinations…</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="featured-destinations-section">
        <div className="featured-container">
          <div className="featured-header">
            <h2 className="heading-playfair-italic main-title">Best Wedding Places in India</h2>
          </div>
          <div className="error-state">Failed to load destinations. Please try again later.</div>
        </div>
      </section>
    );
  }

  return (
    <section className="featured-destinations-section">
      <div className="featured-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="featured-header"
        >
          <span className="sub-heading">Curated Venues</span>
          <h2 className="heading-playfair-italic main-title">
            Best Wedding Places in India
          </h2>
        </motion.div>

        {/* Cards Grid */}
        <div className="destinations-grid">
          {destinationsData.map((destination, index) => (
            <motion.article
              key={destination.id || destination.name || index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="destination-card"
            >
              <a href={destination.link} className="card-link-wrapper">
                {/* Full‑cover image */}
                <div className="card-image-wrapper">
                  <img
                    src={destination.image}
                    alt={destination.name}
                    className="card-bg-image"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x600?text=No+Image';
                    }}
                  />
                </div>

                {/* Top Badge: Package count */}
                {destination.packagesCount > 0 && (
                  <div className="card-badge">
                    {destination.packagesCount} {destination.packagesCount === 1 ? 'Package' : 'Packages'}
                  </div>
                )}

                {/* Gradient overlay */}
                <div className="card-overlay" />

                {/* Text overlay at bottom */}
                <div className="card-content1">
                  {(destination.city || destination.state) && (
                    <div className="card-location">
                      <FiMapPin className="pin-icon" />
                      <span>
                        {[destination.city, destination.state].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}

                  <h3 className="heading-playfair-italic card-title">
                    {destination.name}
                  </h3>

                  {destination.shortDescription && (
                    <p className="card-description">
                      {destination.shortDescription}
                    </p>
                  )}

                  <div className="read-more-link">
                    <span className="read-more-text">Explore Venue</span>
                    <FiArrowRight className="arrow-icon-small" />
                  </div>
                </div>
              </a>
            </motion.article>
          ))}
        </div>

        {/* View All */}
        <div className="view-all-wrapper">
          <a href="/wedding-destinations" className="view-all-link">
            <span>View All Destinations</span>
            <FiArrowRight className="arrow-icon-large" />
          </a>
        </div>
      </div>
    </section>
  );
}